import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import {
  AI_NO_REPLY_TAG_NAME,
  SPAM_TAG_NAME,
} from "@redotech/redo-support-tags-common/system-tag-names";
import { DefaultRule } from "../types";

/**
 * Default rule: Send AI response on a Shopper Agent conversation
 *
 * Shopper Agent owns its own conversations, so they reply on the agent's gate
 * rather than the merchant's Support AI channel list — a merchant who never
 * enabled AI autopilot for SMS still gets agent replies on agent threads.
 *
 * Every gate is a condition on one flat step, so the rule prunes in-process
 * (`evaluateRulePrefilter` reads exactly this step) and can only ever reply:
 *   - the thread is the live continuation lane of a `shopper_agent_conversation`
 *   - AI owns the ticket, so the agent never answers over a human
 *   - the ticket is not tagged SPAM or AI_NO_REPLY
 *
 * Always enabled and hidden: there is no merchant-facing config, and none is
 * read. When the team turns Shopper Agent off, the lane condition goes false.
 *
 * Owns agent lanes outright. `sortIndex: 1` runs it ahead of every other
 * `CONVERSATION_MESSAGE_CREATED` rule, and
 * `default-send-ai-response-on-customer-reply` carries the mirror-image
 * `SHOPPER_AGENT_CONVERSATION IS_NOT` guard on both of its branches, so on an
 * agent lane that rule neither replies nor unassigns the AI out from under this
 * one. The two can no longer both reach `START_AI_AGENT_EXECUTION`.
 */
export const sendAiResponseOnShopperAgentConversationRule = {
  id: "default-send-ai-response-on-shopper-agent-conversation",
  name: "Send AI response on a Shopper Agent conversation",
  description:
    "When a customer replies on a Shopper Agent conversation the AI owns, and the ticket isn't tagged SPAM or AI_NO_REPLY, have the agent respond.",
  hidden: true,
  triggerType: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,

  defaultConfig: {
    defaultRuleId: "default-send-ai-response-on-shopper-agent-conversation",
    enabled: true,
    // Execution order only: hidden rules are excluded from
    // `maxVisibleDefaultRuleSortIndex`, so the merchant's list is unchanged.
    sortIndex: 1,
    configurations: {},
  },

  template: {
    name: "Send AI response on a Shopper Agent conversation",
    description:
      "When a customer replies on a Shopper Agent conversation the AI owns, and the ticket isn't tagged SPAM or AI_NO_REPLY, have the agent respond.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
        nextId: "shopper-agent-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "shopper-agent-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.SHOPPER_AGENT_CONVERSATION,
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [SPAM_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [AI_NO_REPLY_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [{ actionType: RuleActionType.START_AI_AGENT_EXECUTION }],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule;
